import _ from 'lodash';
import * as ExcelJS from 'exceljs/dist/exceljs';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CoreModule } from 'app/core/core.module';
import { IconModule } from 'app/icon/icon.module';

@Component({
  selector: 'app-volunteers-import-xlsx',
  standalone: true,
  imports: [CommonModule, CoreModule, IconModule, FormsModule, ReactiveFormsModule],
  templateUrl: './volunteers-import-xlsx.component.html',
  styleUrl: './volunteers-import-xlsx.component.scss'
})
export class VolunteersImportXlsxComponent implements OnInit {

  @Input()
  public import: FormControl = null;

  @Input()
  public existingVolunteerList: any[] = [];

  @Output()
  public onChange: EventEmitter<any> = new EventEmitter();

  public extensions: string[] = ['xlsx'];

  public errors: string[] = [];
  private takeUntilSubject: Subject<any> = new Subject();

  constructor(
    
  ) { }

  ngOnInit(): void {
    this.import.valueChanges.pipe(
      filter(value => _.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.errors = [];
    });

    this.import.valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe((file: File) => {
      this.onReadFile(file);
    });
  }

  onReadFile(file: File) {
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      // read workbook and grab the first sheet
      const bstr: string = e.target.result;
      const workbook = new ExcelJS.Workbook();

      workbook.xlsx.load(bstr).then(() => {
        const workSheets = workbook.worksheets;
        let firstSheet = workbook.getWorksheet("Volunteers") || null;

        if(firstSheet === null && workSheets.length > 1) {
          this.errors = ['Multiple sheets are detected. Please delete extra sheets and try again.'];
          return;
        }

        if(firstSheet === null) {
          firstSheet = workSheets[0] || null;
        }

        if(firstSheet === null) {
          this.errors = ['Error reading the file. Please make sure it is a valid Excel file.'];
          return;
        }
        
        const itemsSheetColumns = [
          { header: 'Email', key: 'email' },
        ]

        let data = [];
        let columns = [];
        let isFirstRow = true;

        firstSheet.eachRow((rawRow) => {
          let row = []
          let value = null; 

          rawRow.eachCell({ includeEmpty: true }, function(cell, colNumber) {
            if(!isFirstRow) {
              return;
            }
            const value = cell.value || '';
            const filteredValue = value.replace(/\s+/g, ' '); // replace all spaces with one space
  
            const itemSheetColumn = itemsSheetColumns.find(itemsSheetColumn => {
              return itemsSheetColumn.header === filteredValue;
            })

            columns.push({...itemSheetColumn, colNumber});
          });

          isFirstRow = false;

          // get the value of each cell
          rawRow.eachCell({ includeEmpty: true }, function(cell) {
            const cellValue = cell.value || '';
            
            switch (cell.type) {
              case ExcelJS.ValueType.RichText:
                cellValue.richText.forEach(val => {
                  value += val.text;
                });
                break;
              case ExcelJS.ValueType.Hyperlink:
                value = cellValue.text || '';
                break;
              default:
                value = cellValue;
                break;
            }

            row.push(value);
          });

          let values = _.map(row, (value, index) => {
            const str = new String(value);
            const column = columns.find((column) => {
              return column.colNumber === index + 1;
            }) || null;

            if(column === null) {
              return null;
            }

            return { 
              field: column.key,
              value: str.toString()
            };
          });

          values = _.reject(values, value => value === null);
          
          data.push(values);
        });

        data = _.map(data, row => {
          return _.map(row, cell => {
            return {...cell, value: _.trim(cell.value) };
          });
        });
        data = _.filter(data, row => {
          for(let cell of row) {
            if(cell.value !== '') {
              return true;
            }
          }
          return false;
        });

        this.errors = [];
        let result = data.slice(1) || []

        if(result.length < 1) {
          this.errors = ['Could not find Items'];
          return;
        } 
        
        // deduplicate email list
        const uniqueEmailsMap = {};
        result = _.filter(result, (obj) => {
          const email = obj[0].value.trim().toLowerCase();
          if (uniqueEmailsMap[email]) {
            return false; 
          } else {
            uniqueEmailsMap[email] = true;
            return true;
          }
        });
        
        if (result.length > 100) {
          this.errors = ['Up to 100 volunteers allowed'];
          return;
        }

        let atLeastOneEmailValid = false;
        const volunteerEmails = [];
        const emailCounts = _.countBy(result, (obj) => obj[0].value.trim().toLowerCase());

        _.forEach(result, (obj, row) => {
          const email = obj[0].value.trim().toLowerCase();
          let errorMessage = '';

          if (!window['utils'].validateEmail(email)) {
            errorMessage = `Invalid Email`;
          }

          // check if email exists in existing list
          const emailAlreadyExist = this.existingVolunteerList.find(item => item.email === email);
          if (emailAlreadyExist) {
            errorMessage = `Volunteer with this email already exists`;
          }

          // check if email exists in imported list
          if (emailCounts[email] > 1) {
            errorMessage = `Duplicated email`;
          }

          const valid = errorMessage === '';
          if (valid) {
            atLeastOneEmailValid = true;
          }

          const emailObj = {
            email,
            status: valid ? 1 : 0,
            statusMessage: `<span class="body-2 ${valid ? 'green' : 'red'}-text">${valid ? 'Ready to be uploaded' : errorMessage}</span>`
          }
          volunteerEmails.push(emailObj);
        });

        this.emitValue(volunteerEmails, atLeastOneEmailValid)
      });
    };
    reader.readAsBinaryString(file);
  }

  emitValue (emailList, isValid) {
    this.onChange.next({
      volunteerEmails: emailList,
      isValid
    });
  }

  onSelect(file: File) {
    this.import.setValue(file);
  }
}
